import { com } from '@eidu/web'
import { useQuery } from '@tanstack/react-query'
import { VersionRecord } from '../../../../../io/api/entity/data/listVersions'
import fetchVersionSnapshot from '../../../../../io/api/entity/data/fetchVersionSnapshot'
import EntityWithLabelAndRelated from '../../../../../domain/entity/EntityWithLabelAndRelated'
import useVersions from './useVersions'
import diffEntityVsSnapshot, { EntitySnapshotDiff } from './entitySnapshotDiff'
import EntitySnapshot = com.eidu.entity.publish.EntitySnapshot

export type PublishStatus =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | {
      state: 'draft'
      reason: 'never-published'
      latest?: undefined
      latestSnapshot?: undefined
      diff?: undefined
    }
  | {
      state: 'draft'
      reason: 'unpublished-changes'
      latest: VersionRecord
      latestSnapshot: EntitySnapshot
      diff: EntitySnapshotDiff
    }
  | {
      state: 'published'
      latest: VersionRecord
      latestSnapshot: EntitySnapshot
    }

type UsePublishStatusArgs = {
  entity: EntityWithLabelAndRelated
}

const usePublishStatus = ({ entity }: UsePublishStatusArgs): PublishStatus => {
  const { records, loading: versionsLoading, error: versionsError } = useVersions(entity.entity.id)
  const latest = records?.[0]

  const snapshotQuery = useQuery({
    queryKey: ['versionSnapshot', latest?.snapshotResourceKey],
    queryFn: () => fetchVersionSnapshot(latest!.snapshotResourceKey),
    enabled: !!latest,
  })

  if (versionsLoading) return { state: 'loading' }
  if (versionsError) return { state: 'error', message: versionsError.message }
  if (!records) return { state: 'loading' }
  if (!latest) return { state: 'draft', reason: 'never-published' }

  if (snapshotQuery.isLoading) return { state: 'loading' }
  if (snapshotQuery.error) return { state: 'error', message: snapshotQuery.error.message }
  if (!snapshotQuery.data) return { state: 'loading' }

  const latestSnapshot = snapshotQuery.data
  const diff = diffEntityVsSnapshot(entity.entity, latestSnapshot)
  if (diff.equal) return { state: 'published', latest, latestSnapshot }
  return { state: 'draft', reason: 'unpublished-changes', latest, latestSnapshot, diff }
}

export default usePublishStatus
